import React, { useEffect, useState } from 'react';

import {
  ActionButtonContainer,
  ButtonContainer,
  DropdownContainer,
  Label,
  MainContainer,
  OptionStyle,
  Select,
  StatusText,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  Title,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import MyTasksCreateModal from '../MyTasksCreateModal/MyTasksCreateModal';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import {
  formatDateTime,
  isTaskActive,
} from '../../../../helpers/dateTimeHelper';
import {
  cropConnectApiClient,
  CropConnectCountry,
  CropConnectTaskDto,
} from '../../../../services/cropConnectApiClient';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';

const MyTasksAdmin: React.FC = () => {
  const { t } = useI18n();

  const [selectedCountry, setSelectedCountry] = useState('All'); // Default to Germany

  const countries = [
    { code: 'All', name: 'All' },
    { code: 'DE', name: 'Germany' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'PL', name: 'Poland' },
    { code: 'SE', name: 'Sweden' },
  ];
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CropConnectTaskDto | null>(
    null
  );
  const [taskToRemove, setTaskToRemove] = useState<CropConnectTaskDto | null>(
    null
  );
  const [tasks, setTasks] = useState<CropConnectTaskDto[]>([]);

  const handleTaskEdit = (task: CropConnectTaskDto) => {
    setSelectedTask(task);
    setCreateModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleTaskRemove = (task: CropConnectTaskDto) => {
    setTaskToRemove(task);
    setConfirmationModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const confirmRemoveTask = async () => {
    if (taskToRemove) {
      await cropConnectApiClient.deleteTask(taskToRemove.accessId!);
      setTasks(tasks.filter((task) => task.accessId !== taskToRemove.accessId));
      setTaskToRemove(null);
      setConfirmationModalOpen(false);
      document.body.style.overflow = 'overlay';
    }
  };

  const fetchTasks = async () => {
    if (selectedCountry === 'All') {
      const allTasks = await cropConnectApiClient.all();
      if (allTasks && allTasks.data) {
        setTasks(allTasks.data);
      }
    } else {
      const tasks = await cropConnectApiClient.getTasksForCountry(
        selectedCountry as CropConnectCountry
      );
      if (tasks && tasks.data) {
        setTasks(tasks.data);
      }
    }
  };
  useEffect(() => {
    fetchTasks();
  }, [selectedCountry]);

  return (
    <>
      <Title>{t(TranslationKeys.my_tasks_admin_title)}</Title>
      <MainContainer>
        <ButtonContainer>
          <DropdownContainer>
            <Label htmlFor="country">
              {t(TranslationKeys.my_tasks_admin_select_country_label)}
            </Label>
            <Select
              id="country"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              {countries.map((country) => (
                <OptionStyle key={country.code} value={country.code}>
                  {country.name}
                </OptionStyle>
              ))}
            </Select>
          </DropdownContainer>
          <ActionButton
            variant="accept-blue"
            buttonText={t(TranslationKeys.my_tasks_admin_create_button)}
            onClickAction={() => {
              setCreateModalOpen(true);
              document.body.style.overflow = 'hidden';
            }}
          />
        </ButtonContainer>
        <Table>
          <thead>
            <TableRow>
              <TableHeader>
                {t(TranslationKeys.my_tasks_table_header_headline)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.my_tasks_table_header_type)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.my_tasks_table_header_start_date)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.my_tasks_table_header_end_date)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.my_tasks_table_header_status)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.my_tasks_table_header_action)}
              </TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {tasks
              .sort(
                (a, b) =>
                  new Date(a.endDate ?? '').getTime() -
                  new Date(b.endDate ?? '').getTime()
              )
              .map((task) => (
                <TableRow key={task.accessId}>
                  <TableCell>{task.propertyBag?.headline}</TableCell>
                  <TableCell>{task.type}</TableCell>
                  <TableCell>{formatDateTime(task.startDate ?? '')}</TableCell>
                  <TableCell>{formatDateTime(task.endDate ?? '')}</TableCell>
                  <TableCell>
                    <StatusText isValid={isTaskActive(task)}>
                      {isTaskActive(task)
                        ? t(TranslationKeys.my_tasks_valid)
                        : t(TranslationKeys.my_tasks_expired)}
                    </StatusText>{' '}
                  </TableCell>
                  <TableCell>
                    <ActionButtonContainer>
                      <ActionButton
                        variant="accept-blue"
                        buttonText={t(
                          TranslationKeys.my_tasks_admin_action_edit_button
                        )}
                        onClickAction={() => handleTaskEdit(task)}
                      />
                      <ActionButton
                        variant="abort"
                        buttonText={t(
                          TranslationKeys.my_tasks_admin_action_delete_button
                        )}
                        onClickAction={() => handleTaskRemove(task)}
                      />
                    </ActionButtonContainer>
                  </TableCell>
                </TableRow>
              ))}
          </tbody>
        </Table>
      </MainContainer>
      <MyTasksCreateModal
        task={selectedTask}
        isOpen={isCreateModalOpen}
        onClose={() => {
          setSelectedTask(null);
          setCreateModalOpen(false);
          document.body.style.overflow = 'overlay';
        }}
        onTaskSuccessfulUpsert={async () => {
          await fetchTasks();
        }}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          document.body.style.overflow = 'overlay';
        }}
        onConfirm={confirmRemoveTask}
        message={t(TranslationKeys.my_tasks_admin_delete_confirmation)}
      />
    </>
  );
};

export default MyTasksAdmin;
